<template>
  <v-list
    right
    color="transparent"
  >
    <v-list-item-group class="d-flex justify-end" style="gap: 16px;">
      <v-list-item-icon v-for="action in visibleActions" :key="action.mode" class="mx-0">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              :disabled="isActionDisabled(action)"
              icon
              v-bind="getRouterLink(action)"
              v-on="on"
              @click="handleActionClick(action)"
            >
              <v-icon>mdi-{{ action.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ action.label }}</span>
        </v-tooltip>
      </v-list-item-icon>
      <v-list-item-icon v-if="visibleMoreActions?.length" class="mx-0">
        <v-menu offset-y>
          <template #activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="action in visibleMoreActions"
              :key="action.mode"
              :disabled="isActionDisabled(action)"
              v-bind="getRouterLink(action)"
              @click="handleActionClick(action)"
            >
              <v-list-item-icon>
                <v-icon>mdi-{{ action.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ action.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-list-item-icon>
    </v-list-item-group>
  </v-list>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/index';
import { isPublicationDateValid, isToday } from '@/configs/helper.js';

export default {
  name: 'ActionsContent',
  props: {
    actions: {
      type: Array,
      default: () => []
    },
    moreActions: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      default: () => ({})
    },
    contentTypeLabel: {
      type: String,
      default: null
    },
    contentTypeDisplay: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['canPerformAction']),
    visibleActions () {
      return this.actions.filter(action => !this.isActionHidden(action));
    },
    visibleMoreActions () {
      return this.moreActions.filter(action => !this.isActionHidden(action));
    },
    canUpdateStartPublicationDate () {
      if (this.item.status !== 'published') return false;
      if (this.item.endPublicationDate && isToday(this.item.endPublicationDate)) return false;
      if (this.item.startPublicationDate && this.item.endPublicationDate && !isPublicationDateValid(this.item.startPublicationDate, this.item.endPublicationDate)) return false;

      return true;
    },
    canDeleteJob () { return true; }
  },
  methods: {
    isActionDisabled (action) {
      if (action.disabled) {
        return true;
      }

      if (action.permission && !this.canPerformAction(action.permission)) {
        return true;
      }

      if (action.routeName === 'broadbean-middleware' && !this.user.broadBeanUsername) {
        return true;
      }

      if (action.routeName === 'delete-job' && !this.canDeleteJob) {
        return true;
      }

      return false;
    },
    isActionHidden (action) {
      if (this.$store.getters.isCentralizedWorkspace
        && [
          'broadbean-middleware',
          'update-start-publication-date',
          'duplicate-offer',
          'offer-candidatures'
        ].includes(action.routeName)
      ) {
        return true;
      }

      if (!action?.hideField) return false;
      if (action.routeName === 'delete-job' && !this.canDeleteJob) {
        return true;
      }

      if (action.value === 'updateStartPublicationDate') {
        return !this.canUpdateStartPublicationDate;
      }

      if (!['broadBeanUrl', 'copyOffer', 'editJob', 'generateOffer'].includes(action.value)) return false;

      const hideConditions = Array.isArray(action.hideField) ? action.hideField : [action.hideField];

      return hideConditions.some(condition => {
        const allFieldsHaveValue = condition.fields.every(field => {
          const fieldValue = this.item[field];
          return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
        });

        if (condition.value === true) return allFieldsHaveValue;

        if (condition.value === false) return !allFieldsHaveValue;

        if (condition.value === null) return !allFieldsHaveValue;

        return condition.fields.some(field => this.item[field] === condition.value);
      });
    },
    getRouterLink (action) {
      switch (action.routeName) {
        case 'job':
        case 'offer':
        case 'model_offer':
          return {
            to: {
              name: action.routeName,
              params: {
                contentTypeLabel: this.contentTypeLabel,
                mode: action.mode,
                uuid: this.item.uuid
              }
            }
          };
        case 'jobs':
        case 'offers':
          return { to: { name: action.routeName } };
        case 'offer-candidatures':
          return {
            to: {
              name: 'offer-candidatures',
              params: { offerUuid: this.item.uuid }
            }
          };
        case 'item-link':
          return { href: this.item[action.value], target: '_blank' };
        // Actions handled by click events should return an empty object
        case 'broadbean-middleware':
        case 'duplicate-offer':
        case 'generate-offer':
        case 'update-start-publication-date':
        case 'delete-offer':
        case 'delete-model-offer':
        case 'delete-job':
          return {};
        default:
          console.error(`[ACTIONS_CONTENT] => action.routeName not found => ${action.routeName}`);
          return { to: { name: '404' } };
      }
    },
    handleActionClick (action) {
      // This method triggers the click handlers for non-router-link actions
      const clickHandler = this.setAction(action);
      if (clickHandler && typeof clickHandler.click === 'function') {
        clickHandler.click();
      }
    },
    setAction (action) {
      switch (action.routeName) {
        case 'broadbean-middleware': {
          if (!this.user.broadBeanUsername) return {};

          return {
            click: async () => {
              this.$emit('set-loading', true);
              try {
                const newUrl = await requests.getBroadBeanNewUrl(this.item.broadBeanUuid, this.item.uuid, this.user.broadBeanUsername);

                if (!newUrl) {
                  this.$store.commit('setSnackbar', { text: 'Impossible de récupérer le lien de diffusion', color: 'error' });
                  return;
                }

                window.open(newUrl, '_blank');
              } catch (error) {
                console.error('Error fetching BroadBean status or URL:', error);
              } finally {
                this.$emit('set-loading', false);
              }
            }
          };
        }
        case 'update-start-publication-date': {
          return {
            click: async () => {
              if (!this.canUpdateStartPublicationDate) {
                return;
              }

              this.$emit('set-loading', true);
              try {
                const currentDate = moment().format('YYYY-MM-DDTHH:mm:ssZ');

                const updatedItem = {
                  uuid: this.item.uuid,
                  status: this.item.status,
                  version: this.item.version,
                  startPublicationDate: currentDate
                };

                const response = await requests.saveOffer({
                  input: updatedItem,
                  isEditMode: true
                });

                if (!response.success) {
                  this.$store.commit('setSnackbar', { text: 'Impossible de rafraîchir l\'offre', color: 'error' });
                  return;
                }

                // Update the item in the store
                const itemInStore = this.$store.state.items.find(item => item.uuid === this.item.uuid);
                if (itemInStore) {
                  itemInStore.startPublicationDate = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
                }

                this.$store.commit('setSnackbar', { text: `L’offre <b>'${this.item.wtitle}'</b> a bien été rafraîchie ! La nouvelle date de publication sera visible sur le site carrière dans quelques minutes.`, color: 'success' });
              } catch (error) {
                console.error('Error updating start publication date:', error);
                this.$store.commit('setSnackbar', { text: 'Erreur lors de la mise à jour', color: 'error' });
              } finally {
                this.$emit('set-loading', false);
              }
            }
          };
        }
        case 'delete-offer': {
          return {
            click: async () => {
              this.$emit('set-loading', true);
              try {
                const response = await requests.deleteOffer(this.item.uuid);

                if (!response.success) {
                  this.$store.commit('setSnackbar', { text: 'Impossible de supprimer l\'offre', color: 'error' });
                  return;
                }

                const deletedItem = this.$store.state.items.find(item => item.uuid === this.item.uuid);

                if (deletedItem) {
                  deletedItem.status = 'deleted';
                }

                this.$store.commit('setSnackbar', { text: 'Offre supprimée avec succès', color: 'success' });
              } catch (error) {
                console.error('Error deleting offer:', error);
              } finally {
                this.$emit('set-loading', false);
              }
            }
          };
        }
        case 'delete-model-offer': {
          return {
            click: async () => {
              this.$emit('set-loading', true);
              try {
                const response = await requests.deleteModelOffer(this.item.uuid);

                if (!response.success) {
                  this.$store.commit('setSnackbar', { text: 'Impossible de supprimer le modèle d\'offre', color: 'error' });
                  return;
                }

                const deletedItem = this.$store.state.items.find(item => item.uuid === this.item.uuid);

                if (deletedItem) {
                  deletedItem.status = 'deleted';
                }

                this.$store.commit('setSnackbar', { text: 'Modèle d\'offre supprimé avec succès', color: 'success' });
              } catch (error) {
                console.error('Error deleting model offer:', error);
              } finally {
                this.$emit('set-loading', false);
              }
            }
          };
        }
        case 'delete-job': {
          return {
            click: async () => {
              if (!this.canDeleteJob) {
                return;
              }

              this.$emit('set-loading', true);
              try {
                const response = await requests.deleteJob(this.item.uuid);

                if (!response.success) {
                  this.$store.commit('setSnackbar', {
                    text: 'Impossible de Retirer cet intitulé de poste',
                    color: 'error'
                  });
                  return;
                }

                const deletedItem = this.$store.state.items.find(item => item.uuid === this.item.uuid);

                if (deletedItem) {
                  deletedItem.status = 'deleted';
                }

                this.$store.commit('setSnackbar', { text: 'Besoin supprimée avec succès', color: 'success' });
              } catch (error) {
                console.error('Error deleting job:', error);
              } finally {
                this.$emit('set-loading', false);
              }
            }
          };
        }
        case 'duplicate-offer': {
          return {
            click: async () => {
              this.$store.commit('setDialog', { show: true, title: 'Dupliquer l\'offre', text: `Souhaitez-vous confirmer la duplication de l'offre: ${this.item.wtitle}?`, handleAccept: this.duplicateOffer });
            }
          };
        }
        case 'generate-offer': {
          return {
            click: async () => {
              this.$store.commit('setDialog', { show: true, title: 'Générer l\'offre', text: `Souhaitez-vous confirmer la génération de l'offre: ${this.item.wtitle}?`, handleAccept: this.generateOffer });
            }
          };
        }
        default:
          return {};
      }
    },
    async duplicateOffer () {
      this.$emit('set-loading', true);
      const response = await requests.duplicateOffer({ uuid: this.item.uuid, workspace: this.item.workspace });

      if (!response.success) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de la duplication de l\'offre', color: 'error' });
        return;
      }

      this.$store.commit('setSnackbar', { text: 'Offre dupliquée avec succès', color: 'success' });
      this.$router.push({ name: 'offer', params: { contentTypeLabel: 'offres', mode: 'vue', uuid: response.data } });
      this.$emit('set-loading', false);
    },
    async generateOffer () {
      this.$emit('set-loading', true);
      try {
        const { uuid, workspace } = this.item;
        const response = await requests.saveJob({
          input: {
            uuid,
            generatedOffer: true,
            workspace,
            status: 'published'
          },
          isEditMode: true
        });

        if (!response.success) {
          this.$store.commit('setSnackbar', { text: 'Erreur lors de la génération de l\'offre', color: 'error' });
          return;
        }

        this.$store.commit('resetDialog');
        this.$store.commit('setSnackbar', { text: 'Génération de l\'offre en cours...', color: 'info', timeout: -1 });
        await new Promise(resolve => { setTimeout(resolve, 7000); });
        this.$store.commit('setSnackbar', { text: 'Offre générée avec succès.', color: 'success' });
        if (this.$route.name !== 'offers') {
          this.$router.push({ name: 'offers', params: { contentTypeLabel: 'offres' } });
        }
      } catch (error) {
        console.error('Error generating offer:', error);
      } finally {
        this.$emit('set-loading', false);
      }
    }
  }
};
</script>
